import { useEffect, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";

// Poll interval: 2 minutes
const POLL_MS = 120_000;
// How long to keep the "active-ticket" cache before re-checking with the server
const ACTIVE_REFRESH_MS = 60_000;

/**
 * Global background watcher. Whenever the technician has an active
 * clock-in on a ticket, we ask the browser for a GPS reading every
 * ~2 minutes and post it to /api/geofence/ping. Backend decides whether
 * to alert (distance > radius) and stores an entry in db.alerts for
 * the manager to review.
 */
export default function GeofenceGuard() {
  const { user } = useAuth();
  const { t } = useLang();
  const timerRef = useRef(null);
  const activeRef = useRef(null); // { active, ticket_number, ... }
  const lastActiveAt = useRef(0);

  useEffect(() => {
    if (!user) return;
    // Only run for roles that can be on-site
    if (!["technician", "admin", "manager", "pm"].includes(user.role)) return;

    const runOnce = async () => {
      // Refresh active status periodically
      if (!activeRef.current || Date.now() - lastActiveAt.current > ACTIVE_REFRESH_MS) {
        try {
          activeRef.current = await api.get("/geofence/active");
          lastActiveAt.current = Date.now();
        } catch { activeRef.current = { active: false }; }
      }
      const info = activeRef.current;
      if (!info?.active || !info?.enabled) return;

      // Ask the browser for a GPS reading
      if (!("geolocation" in navigator)) return;
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const res = await api.post("/geofence/ping", {
            lat: pos.coords.latitude, lng: pos.coords.longitude,
          });
          if (res?.alerted) {
            toast.warning(
              t("geofenceOutMsg") ||
                `You are ${res.distance_miles} mi from the job site (radius ${res.radius_miles} mi). Please clock out if you're leaving.`,
              { duration: 12_000, id: "geofence-alert" }
            );
            // Force active refresh in case they clock out from the alert
            lastActiveAt.current = 0;
          }
        } catch { /* silent */ }
      }, () => { /* denied or unavailable — silent */ },
        { enableHighAccuracy: false, maximumAge: 60_000, timeout: 30_000 });
    };

    // Run immediately and then on interval
    runOnce();
    timerRef.current = setInterval(runOnce, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [user, t]);

  return null;
}
